import { PropsWithChildren } from "react";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";

import FormCondition from "@components/forms/FormCondition";
import {
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileUseCaseEnum,
  GetCareProfileFieldRulesQuery
} from "@typing/Generated";

type CareProfile = {
  useCase: CareProfileUseCaseEnum | null;
};

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props<FormData extends FieldValues> = PropsWithChildren<{
  careProfile: CareProfile;
  condition?: boolean;
  fieldName: CareProfileFieldNameEnum;
  fieldRules: CareProfileFieldRules;
  form: UseFormReturn<FormData>;
  formField: Path<FormData>;
  otherField?: Path<FormData>;
  otherValue?: string;
  scope: CareProfileFieldScopeEnum;
}>;

const CareProfileFormCondition = <FormData extends FieldValues>({
  careProfile,
  children,
  condition = true,
  fieldName,
  fieldRules,
  form,
  formField,
  otherField,
  otherValue,
  scope
}: Props<FormData>) => {
  const otherFieldValue = otherField && form.watch(otherField);

  const rule = fieldRules.find(rule => rule.scope === scope && rule.field === fieldName);

  let enabled = true;

  if (rule && careProfile.useCase) {
    if (!rule.useCases.includes(careProfile.useCase)) {
      enabled = false;
    }
  }

  if (!condition) {
    enabled = false;
  }

  if (otherField && otherValue) {
    if (Array.isArray(otherFieldValue)) {
      if (!otherFieldValue.includes(otherValue)) {
        enabled = false;
      }
    } else {
      if (otherFieldValue !== otherValue) {
        enabled = false;
      }
    }
  }

  return (
    <FormCondition<FormData> condition={enabled} fieldName={formField} form={form}>
      {children}
    </FormCondition>
  );
};

export default CareProfileFormCondition;
